import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  FiUser, FiChevronLeft, FiSearch, FiCheck,
  FiPlay, FiSend, FiRotateCcw, FiXCircle, FiX, FiClock, FiFolder
} from 'react-icons/fi';
import { useTicketStore } from '../../store/ticketStore';
import { useAuthStore } from '../../store/authStore';
import { useShallowStore } from '../../hooks/useShallowStore';
import api from '../../services/api';
import { CommentThread, ActivityTimeline } from './TicketConversation.jsx';
import ProjectTickets, { TicketImages } from './ProjectTickets.jsx';
import { SkeletonLine, SkeletonBlock, SkeletonCircle } from '../common/Skeleton.jsx';
import { STATUS_LABELS, STAGES, NEXT_ACTION, stageOf, stageIndex, assigneesOf, personName } from './ticketStatus';
import './Tickets.css';

// Can create/assign tickets and set any status
const MANAGER_ROLES = ['ceo', 'co_ceo', 'company_manager', 'sales_head', 'production_head', 'manager'];
// Can only work on tickets of their own projects (or assigned to them)
const MEMBER_ROLES = ['employee', 'sales_employee', 'production_employee'];

const NEXT_ICONS = { in_progress: <FiPlay />, completed: <FiCheck />, deployed: <FiSend /> };

const idOf = (x) => String(x?._id || x?.id || x || '');

const ticketDetailSelector = (state) => ({
  fetchTicket: state.fetchTicket,
  currentTicket: state.currentTicket,
  updateStatus: state.updateStatus,
  assignTicket: state.assignTicket,
  addComment: state.addComment,
  loading: state.loading,
});

// Mirrors TicketDetail's layout so the loading state doesn't jump around
const TicketDetailSkeleton = memo(function TicketDetailSkeleton() {
  return (
    <div className="ticket-detail" aria-hidden="true">
      <div className="back-btn-placeholder">
        <SkeletonLine width={130} height={14} />
      </div>
      <div className="ticket-detail-header">
        <SkeletonBlock width={90} height={22} radius={6} style={{ marginBottom: 10 }} />
        <SkeletonLine width="60%" height={22} style={{ marginBottom: 12 }} />
        <div className="ticket-meta-row">
          <SkeletonBlock width={70} height={22} radius={12} />
          <SkeletonBlock width={100} height={22} radius={12} />
        </div>
      </div>
      <div className="ticket-detail-grid">
        <section className="ticket-section area-progress">
          <SkeletonLine width="100%" height={40} />
        </section>
        <div className="ticket-sidebar area-side">
          <div className="ticket-info-card">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="tk-info-row">
                <SkeletonLine width={70} height={11} />
                <SkeletonLine width={90} height={11} />
              </div>
            ))}
          </div>
        </div>
        <section className="ticket-section area-desc">
          <SkeletonLine width={110} height={14} style={{ marginBottom: 14 }} />
          <SkeletonLine width="95%" height={12} style={{ marginBottom: 8 }} />
          <SkeletonLine width="80%" height={12} />
        </section>
        <section className="ticket-section area-comments">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <SkeletonCircle size={28} />
              <SkeletonLine width="60%" height={12} />
            </div>
          ))}
        </section>
      </div>
    </div>
  );
});

// Pending → In Progress → Completed → Deployed. Managers can tap any step to jump there.
function StatusStepper({ status, canJump, busy, onSelect }) {
  const current = stageIndex(status);
  const closed = stageOf(status) === 'closed';
  return (
    <ol className={`ticket-stepper ${closed ? 'is-closed' : ''}`}>
      {STAGES.map((stage, i) => {
        const state = closed ? 'todo' : i < current ? 'done' : i === current ? 'current' : 'todo';
        const content = (
          <>
            <span className="step-dot">{state === 'done' ? <FiCheck /> : i + 1}</span>
            <span className="step-label">{stage.label}</span>
          </>
        );
        return (
          <li key={stage.key} className={`step step-${state}`}>
            {canJump ? (
              <button
                type="button"
                className="step-btn"
                disabled={busy || (state === 'current' && status === stage.key)}
                onClick={() => onSelect(stage.key)}
                title={`Set to ${stage.label}`}
              >
                {content}
              </button>
            ) : (
              <div className="step-btn">{content}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

// Searchable multi-select people list; project members are listed first
function AssigneePicker({ project, employees, initialIds, busy, onSave, onClose }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set(initialIds));
  const memberIds = useMemo(() => new Set((project?.members || []).map(idOf)), [project]);

  const people = useMemo(() => {
    const byId = new Map();
    (project?.members || []).forEach(m => byId.set(idOf(m), m));
    employees.forEach(e => { if (!byId.has(idOf(e))) byId.set(idOf(e), e); });
    const q = query.trim().toLowerCase();
    return [...byId.values()]
      .filter(p => !q || personName(p).toLowerCase().includes(q))
      .sort((a, b) => Number(memberIds.has(idOf(b))) - Number(memberIds.has(idOf(a))));
  }, [project, employees, query, memberIds]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const changed = selected.size !== initialIds.length || initialIds.some(id => !selected.has(id));

  return (
    <div className="assign-picker">
      <div className="assign-search">
        <FiSearch />
        <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search people..." />
        <button type="button" className="tk-icon-btn" onClick={onClose} aria-label="Close"><FiX /></button>
      </div>
      <div className="assign-dropdown">
        {people.map(p => {
          const id = idOf(p);
          const isOn = selected.has(id);
          return (
            <button
              type="button"
              key={id}
              role="checkbox"
              aria-checked={isOn}
              className={`assign-option ${isOn ? 'selected' : ''}`}
              onClick={() => toggle(id)}
            >
              <span className={`assign-checkbox ${isOn ? 'on' : ''}`}>{isOn && <FiCheck />}</span>
              {p.profilePicture
                ? <img src={p.profilePicture} alt="" className="assign-avatar" />
                : <div className="assign-avatar">{p.firstName?.[0]}{p.lastName?.[0]}</div>}
              <span>{personName(p)}</span>
              <small>{memberIds.has(id) ? 'Project member' : p.role?.replace(/_/g, ' ')}</small>
            </button>
          );
        })}
        {people.length === 0 && <p className="empty-text assign-empty">No one found</p>}
      </div>
      <div className="assign-footer">
        <span>{selected.size} selected</span>
        <button type="button" className="btn-primary" disabled={busy || !changed} onClick={() => onSave([...selected])}>
          Save
        </button>
      </div>
    </div>
  );
}

function TicketDetail({ ticketId, onBack, backLabel = 'All Tickets' }) {
  const { fetchTicket, currentTicket, updateStatus, assignTicket, addComment, loading } = useShallowStore(useTicketStore, ticketDetailSelector);
  const { user } = useAuthStore();
  const [employees, setEmployees] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [busy, setBusy] = useState(false);

  const canManage = MANAGER_ROLES.includes(user?.role);
  const myId = idOf(user);

  useEffect(() => {
    fetchTicket(ticketId);
    if (canManage) {
      api.get('/users').then(r => setEmployees(r.data.users.filter(u => u.role !== 'client'))).catch(() => {});
    }
  }, [ticketId, fetchTicket, canManage]);

  const handleStatusChange = useCallback(async (status) => {
    setBusy(true);
    await updateStatus(ticketId, status);
    setBusy(false);
  }, [updateStatus, ticketId]);

  const handleAssign = useCallback(async (userIds) => {
    setBusy(true);
    const updated = await assignTicket(ticketId, userIds);
    setBusy(false);
    if (updated) setShowAssign(false);
  }, [assignTicket, ticketId]);

  const handleComment = useCallback(
    async (content) => Boolean(await addComment(ticketId, content)),
    [addComment, ticketId]
  );

  if (loading || !currentTicket || idOf(currentTicket) !== String(ticketId)) return <TicketDetailSkeleton />;

  const t = currentTicket;
  const stage = stageOf(t.status);
  const assignees = assigneesOf(t);
  const isAssignee = assignees.some(a => idOf(a) === myId);
  const isMember = (t.project?.members || []).some(m => idOf(m) === myId);
  // Mirrors the backend rule: members/assignee of the project, or any non-member company role
  const canWork = !MEMBER_ROLES.includes(user?.role) || isAssignee || isMember;
  const next = NEXT_ACTION[stage];
  const prevStage = STAGES[stageIndex(t.status) - 1];

  return (
    <div className="ticket-detail">
      <button className="tk-back-btn" onClick={onBack}><FiChevronLeft /> {backLabel}</button>

      <div className="ticket-detail-header">
        <div className="ticket-meta-row">
          <span className="ticket-number-badge">{t.ticketNumber}</span>
          <span className={`priority-badge priority-${t.priority}`}>{t.priority}</span>
          <span className={`tk-status-badge status-${t.status}`}>{STATUS_LABELS[t.status]}</span>
        </div>
        <h2>{t.title}</h2>
      </div>

      <div className="ticket-detail-grid">
        {/* Progress + actions */}
        <section className="ticket-section area-progress">
          <h3>Progress</h3>
          <StatusStepper status={t.status} canJump={canManage} busy={busy} onSelect={handleStatusChange} />

          {stage === 'closed' && <p className="ticket-closed-note"><FiXCircle /> This ticket is closed.</p>}

          {canWork && (
            <div className="ticket-actions">
              {next && (
                <button className="btn-primary btn-next" disabled={busy} onClick={() => handleStatusChange(next.to)}>
                  {NEXT_ICONS[next.to]} {next.label}
                </button>
              )}
              {stage === 'deployed' && <span className="ticket-done-note"><FiCheck /> Deployed — all done!</span>}
              {prevStage && stage !== 'closed' && (
                <button className="btn-secondary" disabled={busy} onClick={() => handleStatusChange(prevStage.key)}>
                  <FiRotateCcw /> Back to {prevStage.label}
                </button>
              )}
              {canManage && stage !== 'closed' && (
                <button className="btn-secondary btn-danger-soft" disabled={busy} onClick={() => handleStatusChange('closed')}>
                  <FiXCircle /> Close
                </button>
              )}
              {canManage && stage === 'closed' && (
                <button className="btn-primary" disabled={busy} onClick={() => handleStatusChange('open')}>
                  <FiRotateCcw /> Reopen
                </button>
              )}
            </div>
          )}
        </section>

        {/* Details */}
        <div className="ticket-sidebar area-side">
          <div className="ticket-info-card">
            <h3 className="tk-card-title">Details</h3>
            <div className="tk-info-row"><span><FiFolder /> Project</span><strong>{t.project?.name || '—'}</strong></div>
            <div className="tk-info-row assign-row">
              <span><FiUser /> Assigned to</span>
              {assignees.length === 0 && <strong className="muted">Unassigned</strong>}
              {canManage && !showAssign && (
                <button className="link-btn" onClick={() => setShowAssign(true)}>
                  {assignees.length ? 'Edit' : 'Assign'}
                </button>
              )}
            </div>
            {assignees.length > 0 && (
              <div className="assignee-chips">
                {assignees.map(a => (
                  <span key={idOf(a)} className={`assignee-chip ${idOf(a) === myId ? 'me' : ''}`}>
                    {a.profilePicture
                      ? <img src={a.profilePicture} alt="" />
                      : <span className="assignee-chip-avatar">{a.firstName?.[0]}{a.lastName?.[0]}</span>}
                    {personName(a)}{idOf(a) === myId ? ' (you)' : ''}
                  </span>
                ))}
              </div>
            )}
            {canManage && showAssign && (
              <AssigneePicker
                project={t.project}
                employees={employees}
                initialIds={assignees.map(idOf)}
                busy={busy}
                onSave={handleAssign}
                onClose={() => setShowAssign(false)}
              />
            )}
            <div className="tk-info-row"><span>Raised by</span><strong>{personName(t.createdBy) || '—'}</strong></div>
            {t.client && <div className="tk-info-row"><span>Client</span><strong>{personName(t.client)}</strong></div>}
            <div className="tk-info-row"><span><FiClock /> Created</span><strong>{new Date(t.createdAt).toLocaleDateString()}</strong></div>
            <div className="tk-info-row"><span>Updated</span><strong>{new Date(t.updatedAt).toLocaleDateString()}</strong></div>
          </div>
        </div>

        <section className="ticket-section area-desc">
          <h3>Description</h3>
          <p className="ticket-description">{t.description || 'No description provided.'}</p>
          <TicketImages attachments={t.attachments} />
        </section>

        <CommentThread comments={t.comments || []} myId={myId} onSubmit={handleComment} />

        <ActivityTimeline activity={t.activity || []} />
      </div>
    </div>
  );
}

function TicketManagement() {
  return (
    <ProjectTickets
      renderDetail={(ticketId, onBack, projectName) => (
        <TicketDetail ticketId={ticketId} onBack={onBack} backLabel={projectName} />
      )}
    />
  );
}

export default TicketManagement;
